import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { voiceActorService } from '../../services/voiceActorService';
import { followVoiceActor, unfollowVoiceActor, getFollowerCount, isFollowing } from '../../services/voiceActorService';
import Icon from '../../components/AppIcon';
import UserAvatar from '../../components/UserAvatar';

const PerformerProfile = () => {
  const { performerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [performer, setPerformer] = useState(null);
  const [followerCount, setFollowerCount] = useState(0);
  const [isFollowingPerformer, setIsFollowingPerformer] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  useEffect(() => {
    loadPerformerProfile();
  }, [performerId]);

  const loadPerformerProfile = async () => {
    try {
      setIsLoading(true);
      const [profileData, followerCountData, followingStatus] = await Promise.all([
        voiceActorService?.getVoiceActorProfile(performerId),
        getFollowerCount(performerId),
        isFollowing(performerId)
      ]);
      
      setPerformer(profileData);
      setFollowerCount(followerCountData);
      setIsFollowingPerformer(followingStatus);
    } catch (err) {
      console.error('Error loading performer profile:', err);
      setError('Failed to load performer profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFollowClick = async () => {
    if (!user) {
      setShowSignupPrompt(true);
      return;
    }

    try {
      setIsFollowLoading(true);
      if (isFollowingPerformer) {
        await unfollowVoiceActor(performerId);
        setIsFollowingPerformer(false);
        setFollowerCount(prev => Math.max(0, prev - 1));
      } else {
        await followVoiceActor(performerId);
        setIsFollowingPerformer(true);
        setFollowerCount(prev => prev + 1);
      }
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setIsFollowLoading(false);
    }
  };

  const handleSignupPromptClose = () => {
    setShowSignupPrompt(false);
  };

  const handleSignupRedirect = () => {
    navigate('/registration');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getVocalRangeDisplay = (range) => {
    if (!range) return 'Not specified';
    return range?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase());
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'text-green-400',
      in_progress: 'text-blue-400',
      pending: 'text-yellow-400',
      confirmed: 'text-purple-400',
      cancelled: 'text-red-400'
    };
    return colors?.[status] || 'text-gray-400';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-pink-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-purple-500 border-t-transparent mb-4"></div>
          <p className="text-white/70 text-lg">Loading performer profile...</p>
        </div>
      </div>
    );
  }

  if (error || !performer) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-pink-900 flex items-center justify-center">
        <div className="text-center">
          <Icon name="AlertCircle" size={64} className="text-red-400 mx-auto mb-4" />
          <p className="text-white/70 text-lg mb-6">{error || 'Performer not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-black to-pink-900">
      {/* Hero Section */}
      <div className="relative bg-black/30 border-b border-white/10">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-12">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-8"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Home</span>
          </button>

          <div className="flex flex-col md:flex-row gap-8 items-start">
            {/* Profile Image */}
            <div className="flex-shrink-0">
              {performer?.headshotUrl ? (
                <img
                  src={performer?.headshotUrl}
                  alt={performer?.fullName || performer?.stageName}
                  className="w-48 h-48 rounded-full object-cover border-4 border-purple-500"
                />
              ) : (
                <UserAvatar
                  userId={performerId}
                  name={performer?.fullName || performer?.stageName}
                  size="w-48 h-48"
                  textSize="text-5xl"
                  className="border-4 border-purple-500"
                />
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1">
              <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2">
                {performer?.fullName || performer?.stageName}
              </h1>
              <p className="text-xl text-purple-300 mb-4">Voice Actor</p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center gap-2 text-white/70">
                  <Icon name="Music" size={20} />
                  <span>{getVocalRangeDisplay(performer?.vocalRange)}</span>
                </div>
                {performer?.yearsExperience > 0 && (
                  <div className="flex items-center gap-2 text-white/70">
                    <Icon name="Award" size={20} />
                    <span>{performer?.yearsExperience} years experience</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-white/70">
                  <Icon name="Users" size={20} />
                  <span>{followerCount} {followerCount === 1 ? 'follower' : 'followers'}</span>
                </div>
              </div>

              {/* Follow Button */}
              <button
                onClick={handleFollowClick}
                disabled={isFollowLoading}
                className={`px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 ${
                  isFollowingPerformer
                    ? 'bg-white/10 hover:bg-white/20 text-white border border-white/30' :'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                } ${isFollowLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isFollowLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Icon name={isFollowingPerformer ? 'UserCheck' : 'UserPlus'} size={20} />
                    <span>{isFollowingPerformer ? 'Following' : 'Follow Performer'}</span>
                  </>
                )}
              </button>

              {/* Edit Avatar Button - only shown to the profile owner */}
              {user?.id === performerId && (
                <button
                  onClick={() => navigate('/student-avatar-studio')}
                  className="mt-3 px-8 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30"
                >
                  <Icon name="Pencil" size={20} />
                  <span>Edit Avatar</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-screen-xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Bio & Specializations */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bio Section */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="FileText" size={24} />
                Biography
              </h2>
              <p className="text-white/80 leading-relaxed">
                {performer?.bio || 'No biography available yet.'}
              </p>
            </div>

            {/* Specializations */}
            {performer?.specializations?.length > 0 && (
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <Icon name="Star" size={24} />
                  Specializations
                </h2>
                <div className="flex flex-wrap gap-2">
                  {performer?.specializations?.map((spec, index) => (
                    <span
                      key={index}
                      className="px-4 py-2 bg-purple-600/30 border border-purple-500/50 rounded-full text-white text-sm"
                    >
                      {spec?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Associated Roles & Plays */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="BookOpen" size={24} />
                Associated Roles & Plays
              </h2>
              {performer?.assignments?.length > 0 ? (
                <div className="space-y-4">
                  {performer?.assignments?.map((assignment) => (
                    <div
                      key={assignment?.id}
                      className="bg-black/30 rounded-lg p-4 border border-white/10 hover:border-purple-500/50 transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        {assignment?.playCoverUrl && (
                          <img
                            src={assignment?.playCoverUrl}
                            alt={assignment?.playTitle}
                            className="w-16 h-24 object-cover rounded"
                          />
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1">
                            {assignment?.characterName}
                          </h3>
                          <p className="text-purple-300 mb-2">{assignment?.playTitle}</p>
                          <div className="flex flex-wrap gap-3 text-sm text-white/60">
                            <span className="flex items-center gap-1">
                              <Icon name="FileText" size={14} />
                              {assignment?.lineCount} lines
                            </span>
                            <span className={`flex items-center gap-1 ${getStatusColor(assignment?.status)}`}>
                              <Icon name="Circle" size={14} />
                              {assignment?.status?.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Icon name="BookOpen" size={48} className="text-white/30 mx-auto mb-3" />
                  <p className="text-white/60">No role assignments yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Recent Updates & Stats */}
          <div className="space-y-8">
            {/* Recent Update */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="Clock" size={20} />
                Recent Update
              </h2>
              <p className="text-white/70 text-sm">
                Last active: {formatDate(performer?.updatedAt)}
              </p>
            </div>

            {/* Social Stats */}
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <Icon name="TrendingUp" size={20} />
                Performance Stats
              </h2>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Total Roles</span>
                  <span className="text-2xl font-bold text-purple-400">
                    {performer?.assignments?.length || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Followers</span>
                  <span className="text-2xl font-bold text-pink-400">{followerCount}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/70">Completed</span>
                  <span className="text-2xl font-bold text-green-400">
                    {performer?.assignments?.filter(a => a?.status === 'completed')?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Signup Prompt Modal */}
      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-purple-900 to-pink-900 rounded-2xl p-8 max-w-md w-full border border-white/20 shadow-2xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="UserPlus" size={32} className="text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Sign Up to Follow</h3>
              <p className="text-white/80 mb-6">
                Create an account to follow {performer?.fullName || performer?.stageName} and stay updated with their latest performances!
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleSignupPromptClose}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors border border-white/30"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSignupRedirect}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-all font-semibold"
                >
                  Sign Up Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PerformerProfile;